import { useState } from "react";

import { api } from "../api/rest";
import type { Market, MarketPnlResponse, PaperOrder, PnlLedger, Token } from "../api/types";
import { useMarketPnl, useRefreshPnl } from "../hooks/usePnl";
import { parse, toFixed } from "../lib/money";
import { formatSigned } from "../lib/position";
import {
  ageLabel,
  exitNow,
  entryLabel,
  freshness,
  freshnessTitle,
  hasLedger,
  ledgerNote,
  moneyLabel,
  moneyTone,
  roiLabel,
  settledNote,
  sharesLabel,
  slippageLabel,
  statusLabel,
  toneOf,
  type PnlTone,
} from "../lib/pnl";
import { commandTargetNow } from "../store/useAccounts";
import { useBooks } from "../store/useBooks";
import { useOrders } from "../store/useOrders";
import { Note } from "./Panel";

/**
 * Позиция окна и её итог — секция внутри панели ордеров, а не своя карточка.
 *
 * # Почему внутри «Моих ордеров»
 *
 * Потому что вопрос один и тот же: «что у меня на этом окне». Список отвечает
 * «что я отправил», позиция — «что из этого получилось», и разъехавшись по двум
 * карточкам, они заставляли читать один ответ в двух местах. Отдельной панелью
 * это стояло ровно один день.
 *
 * Вместе с объединением ушёл прежний футер позиций (`PositionsFooter`): он
 * показывал ТУ ЖЕ позицию и её оценку «выходом сейчас по стакану». Оценка не
 * потеряна — она стоит на кнопках продажи в панели быстрых действий, то есть
 * там, где по ней действуют (`lib/position.sellPnl`). Здесь остался другой
 * вопрос, которого не было нигде: сколько будет ПРИ КАЖДОМ исходе окна.
 *
 * # Почему счёт приходит с сервера целиком
 *
 * Одна реализация на систему. Реальные филлы и пробные наливы складывает
 * `internal/marketpnl`, гасит эталонная `walletflow.Settle`; повтори мы формулу
 * здесь, TypeScript и Go разошлись бы на первой правке, а расхождение видно
 * только по деньгам.
 *
 * # Почему секции нет, пока не торговали
 *
 * Две строки «сделок не было» под пустым списком ордеров — это шум в панели,
 * которая теперь стоит в колонке 420 пикселей. Появляется секция вместе с
 * первой сделкой или первой пробой.
 */
export function PositionSummary({
  conditionId,
  tokens,
  market,
}: {
  conditionId: string;
  tokens: Token[];
  /**
   * Рынок — ради ЗАКРЫТИЯ, а не показа: шаг цены, минимальный размер и
   * расписание комиссии нужны, чтобы разрешить выход тем же кодом, которым
   * живут кнопки продажи. Отсутствует — строки закрытия нет, остальное на месте.
   */
  market?: Market | undefined;
}) {
  const pnl = useMarketPnl(conditionId);
  const data = pnl.data;

  if (!data) return null;
  const traded = data.real.trades > 0 || data.paper.trades > 0;
  if (!traded) return null;

  return (
    <div className="mt-3 border-t border-[var(--color-border)] pt-2">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium">Позиция окна</h3>
        <span className="truncate text-xs text-[var(--color-muted)]">
          {resolutionNote(data)}
        </span>
      </div>

      <Mode
        title="Реально"
        ledger={data.real}
        tokens={tokens}
        resolved={data.resolution.resolved}
        market={market}
        conditionId={conditionId}
      />
      <Mode
        title="Симуляция"
        ledger={data.paper}
        tokens={tokens}
        resolved={data.resolution.resolved}
        market={market}
        conditionId={conditionId}
        muted
        /* Закрывать кнопкой можно только ПРОБНУЮ позицию. Настоящую закрывают
           долями 25/50/100 в панели быстрых действий: там у выхода есть свой
           предпросмотр, свои клавиши и своё эхо вердикта биржи, и заводить
           рядом вторую кнопку «продать всё» — значит удваивать путь, которым
           тратятся деньги, ради экономии одного клика. */
        closable
      />

      {data.paper_orders.length > 0 && (
        <PaperJournal rows={data.paper_orders} conditionId={conditionId} tokens={tokens} />
      )}

      <Note label="что здесь считается">
        Цена входа — <b>деньги по задетым уровням стакана плюс комиссия</b>,
        делённые на налив: не цена вершины и не лимит ордера. Итог по каждому
        сценарию — погашение той же формулой, которой считается PnL кошелька и
        которая сверена с числом биржи; ROI берёт знаменателем затраты на
        покупки без комиссии, как в панели накопления. Пробный ордер при этом{" "}
        <b>не двигает рынок</b> и мейкерского входа не пробует вовсе — заявку
        исполняет встречный поток, а не стакан. Смотрите на возраст стакана в
        журнале: счёт, снятый на протухшем снимке, систематически завышен.
      </Note>
    </div>
  );
}

/**
 * Блок одного режима.
 *
 * Строки сценариев исчезают, как только исход подтверждён, и это не экономия
 * места: прогноз, оставленный рядом с итогом, читается как второе мнение о том,
 * что уже случилось.
 */
function Mode({
  title,
  ledger,
  tokens,
  resolved,
  market,
  conditionId,
  muted,
  closable,
}: {
  title: string;
  ledger: PnlLedger;
  tokens: Token[];
  resolved: boolean;
  market: Market | undefined;
  conditionId: string;
  muted?: boolean;
  closable?: boolean;
}) {
  const note = ledgerNote(ledger);
  const books = useBooks((s) => s.books);

  /**
   * Досрочное закрытие. Считается на КАЖДОЙ перерисовке из живого стакана и не
   * запоминается: цена выхода идёт за стаканом тик в тик, а запомненная
   * обещала бы выход, которого уже нет.
   *
   * Строки нет вовсе в двух случаях, и оба не «отказ»: у закончившегося окна
   * (рынок закрыт, выходить некуда) и у ПУСТОЙ позиции — там закрывать нечего, а
   * итог уже виден в сценариях, которые при нулевой позиции сошлись в одно
   * число. «Закрыть сейчас — закрывать нечего» было бы строкой, отвечающей на
   * вопрос, которого никто не задавал.
   */
  const holding = ledger.positions.some((p) => (parse(p.shares) ?? 0) > 0);
  const exit =
    market && !resolved && hasLedger(ledger) && holding
      ? exitNow({ ledger, market, books })
      : null;

  if (!hasLedger(ledger)) {
    // Пустой режим — ОДНА строка: он всё равно обязан быть виден (иначе не
    // видно, что второго счёта просто нет), но места стоит одной строки.
    return (
      <div className="flex items-baseline justify-between gap-2 py-0.5 text-xs">
        <span className={muted ? "text-[var(--color-muted)]" : ""}>{title}</span>
        <span className="text-[var(--color-muted)]">{note}</span>
      </div>
    );
  }

  return (
    <div className="py-0.5">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className={muted ? "text-[var(--color-muted)]" : "font-medium"}>{title}</span>
        <span className="text-[var(--color-muted)]">
          {ledger.trades} сдел. · комиссия{" "}
          {ledger.fee_known ? `$${toFixed(parse(ledger.fee_usd) ?? 0, 2)}` : "—"}
        </span>
      </div>

      {ledger.positions.map((p) => {
        const shares = parse(p.shares);
        if (shares === null || shares === 0) return null;
        return (
          <div
            key={p.asset_id}
            className="flex items-baseline justify-between gap-2 pl-2 text-xs"
          >
            <span>{outcomeOf(tokens, p.asset_id, p.outcome)}</span>
            <span className="num shrink-0 text-[var(--color-muted)]">
              {sharesLabel(p.shares)} @{" "}
              <span title={p.avg_entry ? `точно: ${p.avg_entry}` : "покупок не было"}>
                {entryLabel(p.avg_entry)}
              </span>
            </span>
          </div>
        );
      })}

      {note !== "" ? (
        <p className="pl-2 text-xs text-amber-300">{note}</p>
      ) : ledger.settled ? (
        <div className="flex items-baseline justify-between gap-2 pl-2 text-xs">
          <span className="font-medium">итог</span>
          <span className="num shrink-0">
            <span style={{ color: toneColor(moneyTone(ledger.settled.pnl_usd)) }}>
              {moneyLabel(ledger.settled.pnl_usd)}
            </span>
            <span className="ml-1.5 text-[var(--color-muted)]">
              {roiLabel(ledger.settled.roi)}
            </span>
          </span>
        </div>
      ) : (
        <>
          {exit && (
            <ExitRow
              exit={exit}
              conditionId={conditionId}
              closable={closable === true}
            />
          )}
          {ledger.scenarios.map((s) => (
            <div
              key={s.asset_id}
              className="flex items-baseline justify-between gap-2 pl-2 text-xs"
            >
              <span className="text-[var(--color-muted)]">
                если {outcomeOf(tokens, s.asset_id, s.outcome)}
              </span>
              <span className="num shrink-0">
                <span style={{ color: toneColor(moneyTone(s.pnl_usd)) }}>
                  {moneyLabel(s.pnl_usd)}
                </span>
                <span className="ml-1.5 text-[var(--color-muted)]">{roiLabel(s.roi)}</span>
              </span>
            </div>
          ))}
          {resolved && (
            <p className="pl-2 text-xs text-[var(--color-muted)]">
              {settledNote(ledger, resolved)}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Строка досрочного закрытия: сколько есть ПРЯМО СЕЙЧАС.
 *
 * Стоит между позицией и сценариями резолюции намеренно — читается «что я
 * держу», потом «сколько это стоит сейчас», потом «сколько будет, если
 * досидеть». Разница между этой строкой и сценариями и есть цена ожидания, и до
 * сих пор её считали в уме.
 *
 * Оценка СНИЗУ: цена берётся лимитом тейкера с запасом, как на кнопках продажи,
 * а биржа отдаёт тейкеру улучшение. Обещать больше нельзя — проход по лесенке
 * сделает трейдер, когда ордер действительно уйдёт.
 */
function ExitRow({
  exit,
  conditionId,
  closable,
}: {
  exit: ReturnType<typeof exitNow>;
  conditionId: string;
  closable: boolean;
}) {
  const placePaper = useOrders((s) => s.placePaper);
  const refresh = useRefreshPnl(conditionId);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!exit.ok) {
    // Причина словами, а не пустое место: «нет заявок на покупку» и «меньше
    // минимального размера рынка» — разные состояния, и оба означают, что
    // выйти сейчас нельзя. Число вместо них соврало бы про доступный выход.
    return (
      <div className="flex items-baseline justify-between gap-2 pl-2 text-xs">
        <span className="text-[var(--color-muted)]">закрыть сейчас</span>
        <span className="truncate text-[var(--color-muted)]" title={exit.reason}>
          {exit.reason}
        </span>
      </div>
    );
  }

  /**
   * Закрытие пробой: по ордеру на КАЖДЫЙ исход.
   *
   * Ноги отправляются последовательно, а не пачкой: у каждой свой налив по
   * стакану, и вторая обязана считаться уже после первой — иначе продажа UP и
   * продажа DOWN разошлись бы с журналом по времени, а обе они пробные и на
   * рынок не влияют только по отдельности.
   */
  async function close() {
    if (closing || !exit.ok) return;
    const command = commandTargetNow();
    if (!command.target) {
      setError(command.problem);
      return;
    }
    const target = command.target;
    setClosing(true);
    setError(null);
    const clickedAtMono = performance.now();
    try {
      for (const leg of exit.legs) {
        const res = await placePaper(
          {
            condition_id: conditionId,
            asset_id: leg.assetId,
            side: "SELL",
            price: leg.price,
            size: leg.size,
          },
          target,
          { source: "quick", clickedAtMono },
        );
        if (!res.ok) {
          setError(`${leg.outcome}: ${res.error}`);
          break;
        }
      }
    } finally {
      setClosing(false);
      // Перечитываем ВСЕГДА, в том числе после отказа на второй ноге: первая
      // могла налиться, и позиция на экране обязана это показать.
      refresh();
    }
  }

  return (
    <>
      <div className="flex items-baseline justify-between gap-2 pl-2 text-xs">
        <span className="text-[var(--color-muted)]">
          закрыть сейчас
          {closable && (
            <button
              type="button"
              onClick={() => void close()}
              disabled={closing}
              className="ml-1.5 rounded border border-[var(--color-border)] px-1 text-[10px] hover:text-[var(--color-fg)] disabled:opacity-40"
              title={
                "продать пробой всю пробную позицию: " +
                exit.legs.map((l) => `${l.outcome} ${l.size} @ ${l.price}`).join(", ")
              }
            >
              {closing ? "…" : "закрыть"}
            </button>
          )}
        </span>
        <span
          className="num shrink-0"
          title={
            "оценка СНИЗУ: цена взята лимитом тейкера с запасом, как на кнопках " +
            "продажи, а биржа отдаёт тейкеру улучшение цены. Проход по лесенке " +
            "посчитает трейдер, когда ордер уйдёт"
          }
        >
          {/* formatSigned прямо от Micro, а не через строку и деление на
              миллион: деньги через float — ровно то, что в этом проекте
              запрещено, и «3.2245399999» на экране было бы наименьшей бедой. */}
          <span style={{ color: toneColor(toneOf(exit.pnl)) }}>{formatSigned(exit.pnl)}</span>
          <span className="ml-1.5 text-[var(--color-muted)]">{roiLabel(exit.roi)}</span>
        </span>
      </div>
      {error && <p className="pl-2 text-xs text-[var(--color-sell)]">{error}</p>}
    </>
  );
}

/**
 * Журнал проб — СВЁРНУТЫЙ.
 *
 * Свёрнутый, потому что читают его после разбора, а не в каждой сделке: на
 * вопрос «что у меня в позиции» отвечают строки выше. Развёрнутый, он занимал бы
 * половину колонки постоянно.
 *
 * Возраст стакана внутри стоит отдельной колонкой и с цветом, а не в подсказке:
 * счёт, снятый на протухшем снимке, систематически завышен, и отличить измерение
 * от самообмана можно только по этому числу.
 */
function PaperJournal({
  rows,
  conditionId,
  tokens,
}: {
  rows: PaperOrder[];
  conditionId: string;
  tokens: Token[];
}) {
  const refresh = useRefreshPnl(conditionId);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reset() {
    if (resetting) return;
    setResetting(true);
    setError(null);
    try {
      await api.resetPaper(conditionId);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setResetting(false);
    }
  }

  return (
    <details className="mt-1.5">
      <summary className="select-none text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]">
        Пробные ордера ({rows.length})
      </summary>

      <div className="mt-1 max-h-56 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[var(--color-panel)] text-[var(--color-muted)]">
            <tr>
              <th className="py-1 text-left font-normal">когда</th>
              <th className="py-1 text-left font-normal" title="сторона, исход и — если налив не полный — чем кончилось">что</th>
              <th className="py-1 text-right font-normal">налив</th>
              <th className="py-1 text-right font-normal">вход</th>
              <th
                className="py-1 text-right font-normal"
                title="насколько цена входа хуже вершины стакана"
              >
                снос
              </th>
              <th
                className="py-1 text-right font-normal"
                title="возраст стакана, по которому посчитан налив"
              >
                стакан
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-t border-[var(--color-border)]/50">
                <td className="num py-1 text-[var(--color-muted)]">{clock(o.placed_at)}</td>
                {/* Сторона И ИСХОД вместе: на бинарном окне «куп.» без исхода не
                    описывает ставку вовсе — покупка UP по 0.44 и покупка DOWN по
                    0.44 противоположны. Без исхода строки журнала ещё и не
                    сводились с позицией выше, которая сложена как раз по исходам.

                    Исход НЕ красится: цвет в этой ячейке уже занят стороной
                    (куп. зелёный, прод. красный), и второй цвет рядом читался бы
                    как ещё одно утверждение о направлении сделки. */}
                <td className="py-1 whitespace-nowrap">
                  <span
                    style={{
                      color: o.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)",
                    }}
                  >
                    {o.side === "BUY" ? "куп." : "прод."}
                  </span>{" "}
                  <span className="font-medium">{outcomeOf(tokens, o.asset_id, "?")}</span>
                  {/* Слово статуса — только когда налив НЕ полный. «Налит» в
                      каждой строке повторяло бы то, что и так видно размером
                      налива, и съедало бы место, которого в колонке 420 пикселей
                      нет; зато отказ и частичное исполнение теперь выделяются
                      тем, что подпись у них вообще есть. */}
                  {o.status !== "filled" && (
                    <span
                      className="ml-1 text-[var(--color-muted)]"
                      title={o.reason || statusLabel(o)}
                    >
                      {statusLabel(o)}
                    </span>
                  )}
                  {o.kill_switch && (
                    <span
                      className="ml-1 text-amber-300"
                      title="торговля была остановлена: такой сделки в тот момент случиться не могло"
                    >
                      ⛔
                    </span>
                  )}
                </td>
                <td className="num py-1 text-right">
                  {o.status === "rejected" ? "—" : sharesLabel(o.filled_size)}
                </td>
                <td className="num py-1 text-right" title={o.effective_price ?? "не посчитана"}>
                  {entryLabel(o.effective_price)}
                </td>
                <td className="num py-1 text-right text-[var(--color-muted)]">
                  {slippageLabel(o)}
                </td>
                <td
                  className="num py-1 text-right"
                  style={{ color: freshColor(o.book_age_ms) }}
                  title={freshnessTitle(o.book_age_ms)}
                >
                  {ageLabel(o.book_age_ms)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Причина отказа — под таблицей и только у последней отвергнутой строки:
          в колонку она не влезает, а читают её сразу после нажатия. */}
      {lastRejection(rows) && (
        <p className="mt-1 text-xs text-[var(--color-sell)]">{lastRejection(rows)}</p>
      )}

      <div className="mt-1 flex items-baseline gap-2">
        <button
          type="button"
          onClick={() => void reset()}
          disabled={resetting}
          className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)] disabled:opacity-40"
          title="стереть журнал пробных ордеров этого окна; настоящих сделок не касается"
        >
          {resetting ? "сброс…" : "сбросить симуляцию"}
        </button>
        {error && <span className="text-xs text-[var(--color-sell)]">{error}</span>}
      </div>
    </details>
  );
}

/**
 * Исход по токену: имя из справочника надёжнее пришедшего в ответе.
 *
 * Токена из ДРУГОГО рынка здесь быть не должно, но `fallback` всё равно нужен:
 * подставлять вместо него сам asset_id нельзя — это 77 цифр, и строка журнала
 * разъехалась бы на всю ширину панели.
 */
function outcomeOf(tokens: Token[], assetId: string, fallback: string): string {
  const t = tokens.find((x) => x.asset_id === assetId);
  return (t?.outcome || fallback || assetId).toUpperCase();
}

function resolutionNote(data: MarketPnlResponse): string {
  if (!data.resolution.resolved) return "исход не подтверждён";
  return `победил ${(data.resolution.winner || "?").toUpperCase()}`;
}

function lastRejection(rows: PaperOrder[]): string {
  const bad = rows.find((o) => o.status === "rejected" && o.reason);
  return bad?.reason ?? "";
}

function toneColor(tone: PnlTone): string {
  if (tone === "up") return "var(--color-buy)";
  if (tone === "down") return "var(--color-sell)";
  return "inherit";
}

function freshColor(ageMs: number | null): string {
  switch (freshness(ageMs)) {
    case "live":
      return "var(--color-buy)";
    case "warm":
      return "inherit";
    case "stale":
      return "var(--color-sell)";
    default:
      return "var(--color-muted)";
  }
}

/** Время нажатия «12:34:56». Дата не нужна: секция всегда про одно окно. */
function clock(iso: string): string {
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? "—" : t.toLocaleTimeString("ru-RU", { hour12: false });
}
